/**
 * 配信詳細の「配信情報」カード（設計書 9.1 / html 図1・図2・図4・図7）。
 *
 * 設計書: `docs/design-docs/deliveries/open-click-analytics.md`。
 * 本文中の `設計書 N.M` はこの md、`html N.M` / `html 図N` は同ディレクトリの
 * `open-click-analytics.html` を指す。
 */
import { NO_DATA_COLOR } from "constants/delivery-analytics";
import type { DeliveryAnalytics, DeliveryDetail } from "hooks/use-delivery-api";
import { cn } from "lib/utils";
import { Flag } from "lucide-react";
import type { FC, ReactNode } from "react";
import {
  formatCount,
  formatRate,
  undeliveredChipText,
} from "utils/delivery-analytics";
import { formatDateTime } from "utils/relative-time";

type DeliveryResultCardProps = {
  delivery: DeliveryDetail;
  analytics: DeliveryAnalytics | undefined;
  /** 送信済みで成績を出す配信か（下書き・予約中では出さない） */
  showResult: boolean;
  includeBots: boolean;
  onIncludeBotsChange: (next: boolean) => void;
  /** 不達チップを押したとき（宛先一覧を絞り込む） */
  onUndeliveredClick: () => void;
};

/**
 * 配信詳細の「配信情報」カード（設計書 9.1 / html 図1・図2・図4・図7）。
 * 左「送信の結果」/ 右「受信者の反応」の 2 ブロックに分ける。
 * @param {DeliveryResultCardProps} props 配信・分析データとハンドラ
 * @return {JSX.Element} カード
 */
export const DeliveryResultCard: FC<DeliveryResultCardProps> = ({
  delivery,
  analytics,
  showResult,
  includeBots,
  onIncludeBotsChange,
  onUndeliveredClick,
}) => {
  const chipText = analytics
    ? undeliveredChipText(analytics.undeliveredBreakdown)
    : null;

  return (
    <div className="card bg-base-100 shadow-md mb-4">
      <div className="card-body">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <h3 className="card-title text-base">配信情報</h3>
          {chipText && (
            <button
              type="button"
              onClick={onUndeliveredClick}
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-slate-100 px-3 py-0.5 text-xs font-semibold text-slate-700 hover:bg-slate-200"
            >
              <Flag size={12} aria-hidden />
              {chipText} ›
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          <div>
            <div className="text-sm text-base-content/60">件名</div>
            <div className="font-medium">{delivery.subject}</div>
          </div>
          <div>
            <div className="text-sm text-base-content/60">送信者</div>
            <div>{delivery.fromName || delivery.fromEmail}</div>
          </div>
          <div>
            <div className="text-sm text-base-content/60">配信先グループ</div>
            <div>{delivery.groupName || "-"}</div>
          </div>
          <div>
            <div className="text-sm text-base-content/60">
              {delivery.sentAt ? "配信日時" : "作成日時"}
            </div>
            <div>
              {formatDateTime(delivery.sentAt || delivery.createdAt, "-")}
            </div>
          </div>
        </div>

        {showResult && (
          <div className="mt-4 pt-4 border-t grid grid-cols-1 md:grid-cols-[40%_60%] gap-4">
            <div className="md:pr-4 md:border-r md:border-slate-200">
              <div className="text-sm text-base-content/60 mb-1">
                送信の結果
              </div>
              <div className="flex gap-6">
                <StatBlock label="成功" value={delivery.successCount} />
                <StatBlock
                  label="失敗"
                  value={delivery.failureCount}
                  danger={delivery.failureCount > 0}
                />
                <StatBlock label="合計" value={delivery.totalRecipients} />
              </div>
            </div>

            <div className="md:pl-4">
              <div className="text-sm text-base-content/60 mb-1">
                受信者の反応
              </div>
              {analytics ? (
                <ReactionStats
                  analytics={analytics}
                  includeBots={includeBots}
                  onIncludeBotsChange={onIncludeBotsChange}
                />
              ) : (
                <div className="text-sm text-base-content/50">集計中…</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

type ReactionStatsProps = {
  analytics: DeliveryAnalytics;
  includeBots: boolean;
  onIncludeBotsChange: (next: boolean) => void;
};

/**
 * 右ブロック「受信者の反応」の KPI 4 枚とボット除外タブ。
 * @param {ReactionStatsProps} props 分析データとハンドラ
 * @return {JSX.Element} KPI ブロック
 */
const ReactionStats: FC<ReactionStatsProps> = ({
  analytics,
  includeBots,
  onIncludeBotsChange,
}) => {
  const {
    delivered,
    sent,
    measured,
    openRate,
    clickRate,
    replyRate,
    uniqueOpens,
    uniqueClicks,
    replyRecipients,
  } = analytics;

  const denominator = formatCount(delivered);

  return (
    <>
      {measured && (
        <div className="mt-1 mb-1 flex gap-4">
          <BotTab
            active={!includeBots}
            onClick={() => onIncludeBotsChange(false)}
          >
            人による反応のみ
            <BotTabHint />
          </BotTab>
          <BotTab
            active={includeBots}
            onClick={() => onIncludeBotsChange(true)}
          >
            ボットも含んだ反応
          </BotTab>
        </div>
      )}

      <div className="flex flex-wrap gap-x-6 gap-y-2">
        {/*
          到達は「送信 − 不達」の引き算ではなく、配信システムが受け取ったと
          確認できた宛先数（設計書 3.1）。受理したあとで届かなくなる宛先が
          あるため **到達 + 不達 が送信を超えることがある**ので、
          引き算に見える表記は使わない。「送信 N 中」なら計測開始前の配信
          （引き算で出す例外経路）でも同じ文が成り立つ。
          不達の件数はこのカード上部のチップが持つ。
        */}
        <StatBlock
          label="到達"
          value={formatCount(delivered)}
          sub={`送信 ${formatCount(sent)} 中`}
        />
        <StatBlock
          label="開封率"
          value={formatRate(openRate)}
          muted={openRate === null}
          sub={
            measured
              ? `${formatCount(uniqueOpens)} / ${denominator}`
              : undefined
          }
        />
        <StatBlock
          label="クリック率"
          value={formatRate(clickRate)}
          muted={clickRate === null}
          sub={
            measured
              ? `${formatCount(uniqueClicks)} / ${denominator}`
              : undefined
          }
        />
        {/*
          返信だけ補助行の条件が違うのは、**返信は計測対象外の配信でも数えられる**
          ため（開封・クリックは計測していないので分子が存在しない）。
          `measured` で出し分けると、計測前の配信で返信率だけ数値が出ているのに
          その分子・分母が見えない状態になる。
        */}
        <StatBlock
          label="返信率"
          value={formatRate(replyRate)}
          muted={replyRate === null}
          sub={
            replyRate === null
              ? undefined
              : `${formatCount(replyRecipients)} / ${denominator}`
          }
        />
      </div>
    </>
  );
};

/**
 * 「人による反応のみ」タブの ⓘ。
 * ネイティブの title だと読めない環境があるため、ホバーとフォーカスの両方で
 * 説明を出し、スクリーンリーダー向けにも本文をそのまま置く。
 * @return {JSX.Element} ⓘ と説明
 */
const BotTabHint: FC = () => (
  <span className="group relative ml-1 inline-flex">
    <span
      // biome-ignore lint/a11y/noNoninteractiveTabindex: フォーカスで説明を出すため
      tabIndex={0}
      role="note"
      aria-label="受信側のセキュリティソフトやメールサーバーの自動処理で開かれた・クリックされた分を除いた数字です。"
      className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-full border border-current text-[9px]"
    >
      i
    </span>
    <span
      aria-hidden
      className="pointer-events-none absolute left-1/2 top-full z-10 hidden w-56 -translate-x-1/2 rounded bg-slate-800 px-2 py-1 text-[10px] font-normal text-white group-hover:block group-focus-within:block"
    >
      受信側のセキュリティソフトやメールサーバーの自動処理で開かれた・クリックされた分を除いた数字です。
    </span>
  </span>
);

type BotTabProps = {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
};

/**
 * ボット除外タブの 1 つ。
 * @param {BotTabProps} props 選択状態・ハンドラ・ラベル
 * @return {JSX.Element} タブ
 */
const BotTab: FC<BotTabProps> = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors hover:border-primary hover:bg-primary hover:text-white",
      active
        ? "border-primary bg-primary text-white"
        : "border-base-300 bg-base-100 text-base-content/60 hover:border-primary/80 hover:bg-primary/80 hover:text-white",
    )}
  >
    {children}
  </button>
);

type StatBlockProps = {
  label: string;
  value: number | string;
  sub?: string;
  /** 送信失敗だけに使う赤い数値 */
  danger?: boolean;
  /** 計測対象外の `—` */
  muted?: boolean;
};

/**
 * KPI 1 枚。率の数値には色を付けない（設計書 9.0）。
 * @param {StatBlockProps} props ラベル・値・補助行
 * @return {JSX.Element} KPI
 */
const StatBlock: FC<StatBlockProps> = ({
  label,
  value,
  sub,
  danger,
  muted,
}) => (
  <div>
    <div className="text-xs text-base-content/60">{label}</div>
    <div
      className={cn("text-lg font-bold", danger && "text-error")}
      style={muted ? { color: NO_DATA_COLOR } : undefined}
    >
      {value}
    </div>
    {sub && <div className="text-[11px] text-base-content/50">{sub}</div>}
  </div>
);
